import { Permissions as P, type LeaveAccrual, type LeaveAttachmentRule, type LeaveBalanceCheck, type LeavePolicyDto, type PermissionCode } from '@fas/types';

export const BALANCE_CHECK: Record<LeaveBalanceCheck, { label: string; hint: string }> = {
  NONE: { label: 'Not checked', hint: 'Requests are not compared with the balance.' },
  WARN: { label: 'Warn', hint: 'Requests beyond the balance go through; the approver sees the shortfall.' },
  BLOCK: { label: 'Block', hint: 'Requests beyond the balance are refused.' },
};

export const ACCRUAL: Record<LeaveAccrual, string> = {
  ANNUAL: 'All at the start of the year',
  MONTHLY: 'Monthly (1/12 for each month begun)',
};

export const ATTACHMENT_RULE: Record<LeaveAttachmentRule, string> = {
  NEVER: 'Not needed',
  ALWAYS: 'Always',
  OVER_DAYS: 'For requests longer than…',
};

/** Faculty see their own leave; planners see who is away; leave approvers also see the type (ADR-050). */
export const LEAVE_CALENDAR_READERS: PermissionCode[] = [P.LEAVE_REQUEST, P.LEAVE_APPROVE, P.LEAVE_MANAGE, P.ATTENDANCE_READ_DEPARTMENT, P.SCHEDULE_READ_DEPARTMENT];
/** Balances of everyone in scope. */
export const LEAVE_BALANCE_READERS: PermissionCode[] = [P.LEAVE_APPROVE, P.LEAVE_MANAGE];
/** The Leave area: own balances or everyone's, and the calendar. */
export const LEAVE_AREA: PermissionCode[] = LEAVE_CALENDAR_READERS;

/** "1 day", "2.5 days", "−1 day". */
export function daysText(n: number): string {
  const abs = Math.abs(n);
  return `${n < 0 ? '−' : ''}${abs} day${abs === 1 ? '' : 's'}`;
}

export function entitlementText(p: Pick<LeavePolicyDto, 'annualDays' | 'accrual'>): string {
  if (p.annualDays === null) return 'Not tracked';
  return `${daysText(p.annualDays)} a year${p.accrual === 'MONTHLY' ? ', earned monthly' : ''}`;
}

export function attachmentText(p: Pick<LeavePolicyDto, 'attachmentRule' | 'attachmentOverDays'>): string {
  if (p.attachmentRule === 'ALWAYS') return 'Always';
  if (p.attachmentRule === 'OVER_DAYS') return `Over ${daysText(p.attachmentOverDays ?? 0)}`;
  return 'Not needed';
}

/** What a policy asks of the person filing, in one sentence each. */
export function policyRules(p: LeavePolicyDto, own: boolean): string[] {
  const rules: string[] = [];
  if (p.minNoticeDays > 0) rules.push(own ? `File at least ${daysText(p.minNoticeDays)} before the first day.` : `Faculty file at least ${daysText(p.minNoticeDays)} ahead; HR may record it later.`);
  if (p.attachmentRule === 'ALWAYS') rules.push('Attach a supporting document; approval needs one.');
  if (p.attachmentRule === 'OVER_DAYS') rules.push(`Attach a supporting document for more than ${daysText(p.attachmentOverDays ?? 0)}; approval needs one.`);
  if (p.annualDays !== null && p.balanceCheck !== 'NONE') rules.push(BALANCE_CHECK[p.balanceCheck].hint);
  return rules;
}
